import { buildAPIURL } from "@/lib/api";
import { writeStoredAuthSession } from "@/lib/auth-storage";
import {
  clearHostedAuth0BrowserState,
  writeAuth0SwitchFromEmail,
} from "@/lib/auth0";
import { hostedLogout } from "@/lib/cloud-auth";

type HostedAuth0LogoutOptions = {
  previousUserID?: string;
  markLoggedOut: (value: boolean) => void;
  persistUser: (user: null) => void;
  clearTenantBinding: (userID?: string) => void;
};

let hostedAuth0LogoutInFlight: Promise<void> | null = null;

async function runHostedAuth0Logout({
  previousUserID,
  markLoggedOut,
  persistUser,
  clearTenantBinding,
}: HostedAuth0LogoutOptions): Promise<void> {
  if (typeof window === "undefined") return;

  const returnTo = `${window.location.origin}/ui/login?logged_out=1&prompt=login`;
  const logoutURL = buildAPIURL(
    `/api/auth/hosted/logout?return_to=${encodeURIComponent(returnTo)}&federated=1`,
  );

  // Revoke the managed Railway session before clearing the hosted profile and
  // leaving this origin. Global Auth0 logout must still proceed if either
  // bounded, best-effort request is unavailable.
  await hostedLogout({ timeoutMs: 5000 }).catch(() => undefined);

  const bestEffort = (action: () => void) => {
    try {
      action();
    } catch {
      // Hardened browser storage must not prevent global Auth0 logout.
    }
  };
  try {
    bestEffort(clearHostedAuth0BrowserState);
    bestEffort(() => writeAuth0SwitchFromEmail(""));
    bestEffort(() => markLoggedOut(true));
    bestEffort(() => writeStoredAuthSession(null));
    bestEffort(() => persistUser(null));
    bestEffort(() => clearTenantBinding(previousUserID));
  } finally {
    window.location.assign(logoutURL);
  }
}

export function performHostedAuth0Logout(
  options: HostedAuth0LogoutOptions,
): Promise<void> {
  if (hostedAuth0LogoutInFlight) return hostedAuth0LogoutInFlight;

  let tracked: Promise<void>;
  tracked = runHostedAuth0Logout(options).finally(() => {
    if (hostedAuth0LogoutInFlight === tracked) {
      hostedAuth0LogoutInFlight = null;
    }
  });
  hostedAuth0LogoutInFlight = tracked;
  return tracked;
}
